import { Injectable } from "@angular/core";
import { StorageConstants } from "@app/core/constants/storage-constants";
import { ApiService } from "@app/core/http/api.service";
import { ApiResponse } from "@app/shared/models/ApiResponse";
import {
  AuthProviders,
  AuthProvidersTypes,
} from "@app/shared/models/AuthProviders";
import { LoginAccount } from "@app/shared/models/loginAccount";
import { SsoConfirmResp } from "@app/shared/models/ssoConfirmResp";
import { InAppBrowser } from "@awesome-cordova-plugins/in-app-browser/ngx";
import { environment } from "@env";
import { SharedService } from "../shared/shared.service";
import { StorageService } from "../storage/storage.service";
import { Capacitor } from "@capacitor/core";
import { BehaviorSubject, Observable } from "rxjs";
import { ToastService } from "../toast/toast.service";

@Injectable({
  providedIn: "root",
})
export class LoginAccountService {
  private readonly accountsKey = "accounts";
  private readonly accountOnlyCheck = "accountOnlyCheck";
  defaultAccount$: BehaviorSubject<LoginAccount> = new BehaviorSubject(null);
  authenticatedAccount$: BehaviorSubject<LoginAccount[]> = new BehaviorSubject([]);
  autoAccountAvail$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(
    private storageService: StorageService,
    private apiService: ApiService,
    private sharedSvc: SharedService,
    private storageConstants: StorageConstants,
    private inAppBrowser: InAppBrowser,
    private toastService: ToastService
  ) {
    this.setDefaultAccount();
    this.handleAutoAccount();
  }

  handleAutoAccount(){
    this.authenticatedAccount$.subscribe((accounts: LoginAccount[])=>{
      const autoAvail = accounts.some((account)=> account.auto);
      if(autoAvail){
        this.sharedSvc.accountOnly = true;
        const accountOnlyEle: any = document.getElementById('accountOnly');
        if(accountOnlyEle){
          accountOnlyEle.checked = true;
          accountOnlyEle.disabled = true;
        }
      }
      this.autoAccountAvail$.next(autoAvail);
    })
  }

  async setDefaultAccount(account?){
    let accounts = account ?? (await this.read()) ?? [];
    const defaultAccount = accounts.find((account)=> account.default) || null;
    this.defaultAccount$.next(defaultAccount);
    const authenticateAccount = accounts?.length ? accounts.filter((e) => e.ssoConfirmationResp) : [];
    this.authenticatedAccount$.next(authenticateAccount);
  }

  getDefaultAccount(): Observable<LoginAccount>{
    return this.defaultAccount$;
  }

  async getAccountById(id: number): Promise<LoginAccount>{
    let accounts = await this.read() || [];
    return accounts.find((data) => data.id === id);
  }

 async getActivatedAccount(): Promise<LoginAccount>{
    let accounts = await this.read() || [];
    return accounts.find((data) => data.activeAccount);
  }

  public async read(): Promise<LoginAccount[]> {
    let accounts: Array<LoginAccount> = [];
    accounts = await this.storageService.getJson(this.accountsKey);
    return accounts;
  }
  async getAuthenticatedAccount(): Promise<LoginAccount[]> {
    const lstAccounts = await this.read();  
    return lstAccounts?.length ? lstAccounts.filter((e) => e.ssoConfirmationResp) : [];
  }

  public async create(account: LoginAccount) {
    account.id = Date.now();
    return new Promise(async (resolve, rejects) => {
      let dataSet = (await this.read()) ?? [];
      dataSet.push(account);
      if (account.default === true) {
        await this.updateDefault(dataSet, dataSet.length - 1);
      }
      this.authenticatedAccount$.next(dataSet?.length ? dataSet.filter((e) => e.ssoConfirmationResp) : [])
      await this.storageService.setJson(this.accountsKey, dataSet);
      return resolve(true);
    });
  }
  public async updateSingleRow(account: LoginAccount, index: number) {
    return new Promise(async (resolve, rejects) => {
      let dataSet = (await this.read()) ?? [];

      if (account.default === true) {
        this.removeDefault(dataSet);
      }
      dataSet[index] = account;

      await this.storageService.setJson(this.accountsKey, dataSet);
      return resolve(true);
    });
  }

  public async update(accounts: Array<LoginAccount>) {
    this.setDefaultAccount(accounts)
    return await this.storageService.setJson(this.accountsKey, accounts);
  }

  public async delete() {
    return await this.storageService.remove(this.accountsKey);
  }
  public async updateDefault(
    accounts: LoginAccount[],
    newDefaultIndex: number
  ): Promise<Array<LoginAccount>> {
    this.removeDefault(accounts);
    accounts[newDefaultIndex].default = true;
    return await this.update(accounts);
  }
  public async setAccountOnly(value: boolean) {
    await this.storageService.setJson(this.accountOnlyCheck, { value });
  }
  public async getAccountOnly() {
    return await this.storageService.get(this.accountOnlyCheck);
  }
  private removeDefault(dataSet) {
    const oldDefaultIndex = dataSet.findIndex((acc) => acc.default === true);
    if (oldDefaultIndex != -1) {
      dataSet[oldDefaultIndex].default = false;
    }
  }
  async saveAccount(
    formControll: any,
    isDefault: boolean,
    ssoConfirmResp: SsoConfirmResp,
    lstAuthProviders: Array<AuthProviders>,
    provider : string,
    editIndex?: number,
    authPro?: string
  ) {
    const customerName = formControll.customerName.value;
    const account = formControll.account.value;
    const desc = formControll.description.value;
    const providerIndex = formControll.authProvider.value;
    const providerName = provider || (providerIndex !== "" ? lstAuthProviders[providerIndex].ProviderName : "");
    const authProvider = authPro || (providerIndex !== "" ? lstAuthProviders[providerIndex]?.AuthProvider : "");
    let accountsData: LoginAccount;  
    accountsData = {
      customerName: customerName,
      account: account,
      description: desc,
      default: isDefault,
      providerIndex: providerIndex,
      providerName,
      ssoConfirmationResp: authProvider === "" ? null : ssoConfirmResp,
      authProvider: authProvider
    };

    if (typeof editIndex !== "undefined") {
      await this.updateSingleRow(accountsData, editIndex);
    } else {
      await this.create(accountsData);
    }
  }
  async login(lstAuthProviders: Array<AuthProviders>, indexOfSelectAuth: number) {
    const authProviders: AuthProviders = lstAuthProviders[indexOfSelectAuth];
    const provider  = authProviders.AuthProvider.toLowerCase();
    const browserType = Capacitor.isNativePlatform() ? "_system" : "_self";
    if (provider === AuthProvidersTypes.gsuite.toLowerCase()) {
      const authId = authProviders._id;
      const baseUrl = environment.apiBaseUrl.replace("/public/", "");
      // const url = `${baseUrl}/auth/login?authId=${authId}&orgId=${domainName}&redirectURI=https://${domainName}.eprintitsaas.${urlExtention}/user/sign-in`;
      const url = `${baseUrl}/auth/login?authId=${authId}&orgId=${authProviders.OrgID}&redirectURI=${environment.redirectBaseUrl}${provider}`;

      this.inAppBrowser.create(url, browserType, {});
      
    } else if (provider === AuthProvidersTypes.azureAD.toLowerCase()) {
            
      console.log('auto account value ===> ', this.autoAccountAvail$?.value);
      if(this.autoAccountAvail$?.value){
        return await this.toastService.presentErrorToast('AZURE_ACCOUNT_ADDED_MSG');
      }
      
      const authId = authProviders._id;
      const baseUrl = environment.apiBaseUrl.replace("/public/", "");
      // const url = `${baseUrl}/auth/login?authId=${authId}&orgId=${domainName}&redirectURI=https://${domainName}.eprintitsaas.${urlExtention}/user/sign-in`;
      const url = `${baseUrl}/auth/login?authId=${authId}&orgId=${authProviders.OrgID}&redirectURI=${environment.redirectBaseUrl}${provider}`;

      this.inAppBrowser.create(url, browserType, {});

    } else if (provider === AuthProvidersTypes.saml.toLowerCase()) {
      const authId = authProviders._id;
      const baseUrl = environment.apiBaseUrl.replace("/public/", "");
      // const url = `${baseUrl}/auth/login?authId=${authId}&orgId=${domainName}&redirectURI=https://${domainName}.eprintitsaas.${urlExtention}/user/sign-in`;
      const url = `${baseUrl}/auth/login?authId=${authId}&orgId=${authProviders.OrgID}&redirectURI=${environment.redirectBaseUrl}${provider}`;

      this.inAppBrowser.create(url, "_self", {});
    }
    else if (provider === AuthProvidersTypes.oidc.toLowerCase()) {
      const authId = authProviders._id;
      const baseUrl = environment.apiBaseUrl.replace("/public/", "");
      // const url = `${baseUrl}/auth/login?authId=${authId}&orgId=${domainName}&redirectURI=https://${domainName}.eprintitsaas.${urlExtention}/user/sign-in`;
      const url = `${baseUrl}/auth/login?authId=${authId}&orgId=${authProviders.OrgID}&redirectURI=${environment.redirectBaseUrl}${provider}`;

      this.inAppBrowser.create(url, browserType, {});
    }

  }
  async ssoConfirm(hashId: string, authId: string): Promise<ApiResponse> {
    return new Promise(async (resolve, reject) => {
      try {
        if (!this.sharedSvc.getCustomerDetails) {
          this.sharedSvc.getCustomerDetails =
            await this.sharedSvc.getCustomerInfo();
        }

        const result = await this.apiService.post("sso/confirm", {
          hashId,
          authId,
        });
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
  }
  saveFormState(formControll: any, isDefault: boolean, rowEditIndex: number) {
    //only for web
    const customerName = formControll.customerName.value;
    const account = formControll.account.value;
    const desc = formControll.description.value;
    const authProvider = formControll.authProvider.value;

    let data: LoginAccount = {
      customerName: customerName,
      account: account,
      description: desc,
      default: isDefault,
      providerIndex: authProvider,
      providerName: "",
      rowEditIndex: rowEditIndex,
    };

    this.storageService.setJson(
      this.storageConstants.INPROGRESS_ADD_ACCOUNT_FORM,
      data
    );
  }
}
